import { useEffect, useMemo, useRef, useState } from 'react';
import { FolderLock, FolderPlus, Search } from 'lucide-react';
import { SkeletonLoader } from '../SkeletonLoader';
import { logbookApiService } from '../../services/logbookApi';
import { formatCount, relativeTime } from '../../utils/logbook';
import { cn } from '../../utils/cn';
import type { LogbookCase } from '../../types/logbook';

/**
 * What the operator picked: an existing case, or a new one to open under this title.
 *
 * Nothing is written while choosing. The caller resolves the choice once the record it belongs to
 * exists, so abandoning a half-filled ban form never leaves an empty case behind.
 */
export type CaseChoice =
  | { mode: 'existing'; entry: LogbookCase }
  | { mode: 'new'; title: string };

/** The case index only accepts a whole player UUID, so a half-typed one is not worth asking about. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const caseChoiceIsUsable = (choice: CaseChoice | null): boolean =>
  choice !== null && (choice.mode === 'existing' || choice.title.trim().length > 0);

interface CaseChooserProps {
  value: CaseChoice | null;
  onChange: (choice: CaseChoice | null) => void;
  /** Cases already attached to whatever is being linked; never offered a second time. */
  exclude?: Set<string>;
  /** The player the record is about. Their cases are offered first, because they are the likely one. */
  subject?: string;
  autoFocus?: boolean;
  className?: string;
}

/**
 * Picks the case a note, warning or ban belongs to.
 *
 * The same control serves every surface that attaches a moderation record to a case, so the gesture
 * is the same whether the case is chosen while the ban is being written or added to it afterwards.
 * Opening a case is one of the choices rather than a separate errand somewhere else, because the
 * case usually does not exist until the moment somebody needs to attach something to it.
 */
export const CaseChooser = ({
  value, onChange, exclude, subject, autoFocus, className,
}: CaseChooserProps) => {
  const [cases, setCases] = useState<LogbookCase[] | null>(null);
  /** Ids of cases that already name this player, so they can be floated and marked. */
  const [theirs, setTheirs] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');

  const mode = value?.mode ?? 'existing';
  const named = subject && UUID.test(subject.trim()) ? subject.trim() : undefined;

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      logbookApiService.listCases({ status: 'open', limit: 100 }, controller.signal),
      named
        ? logbookApiService.listCases({ subject: named, limit: 50 }, controller.signal).catch(() => null)
        : Promise.resolve(null),
    ])
      .then(([open, named]) => {
        setTheirs(new Set((named?.cases ?? []).map((entry) => entry.id)));
        // A case that names this player but is already closed still belongs on the list: attaching a
        // later ban to the case that justified it is exactly what a closed case is for.
        const byId = new Map(open.cases.map((entry) => [entry.id, entry]));
        for (const entry of named?.cases ?? []) byId.set(entry.id, entry);
        setCases([...byId.values()]);
      })
      .catch(() => setCases([]));
    return () => controller.abort();
  }, [named]);

  const matching = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const available = (cases ?? []).filter((entry) => !exclude?.has(entry.id));
    const filtered = needle
      ? available.filter((entry) => `${entry.title} ${entry.reference}`.toLowerCase().includes(needle))
      : available;
    return [...filtered].sort((left, right) => {
      const weight = Number(theirs.has(right.id)) - Number(theirs.has(left.id));
      return weight !== 0 ? weight : right.updated_at - left.updated_at;
    });
  }, [cases, query, exclude, theirs]);

  // With nothing to pick from, opening a case is the only move left; do not make them find that out.
  // Once only: switching back to the empty list is still the operator's to make.
  const nudged = useRef(false);
  useEffect(() => {
    if (nudged.current || !cases || cases.length > 0 || value) return;
    nudged.current = true;
    onChange({ mode: 'new', title: '' });
  }, [cases, value, onChange]);

  return (
    <div className={cn('space-y-2', className)}>
      <div className="segmented flex-wrap">
        <button
          type="button"
          className="segmented-item"
          data-active={mode === 'existing'}
          onClick={() => onChange(null)}
        >
          <FolderLock className="w-4 h-4" />
          Existing case
        </button>
        <button
          type="button"
          className="segmented-item"
          data-active={mode === 'new'}
          onClick={() => onChange({ mode: 'new', title: '' })}
        >
          <FolderPlus className="w-4 h-4" />
          New case
        </button>
      </div>

      {mode === 'new' ? (
        <label className="block">
          <span className="block text-xs font-medium text-glass-400 mb-1.5">Case title</span>
          <input
            type="text"
            value={value?.mode === 'new' ? value.title : ''}
            onChange={(event) => onChange({ mode: 'new', title: event.target.value })}
            className="form-input"
            placeholder="Spawn area griefed overnight"
            maxLength={200}
            autoFocus={autoFocus}
          />
        </label>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find a case by title or reference"
              className="form-input pl-9"
              aria-label="Find a case"
              autoFocus={autoFocus}
            />
          </div>

          <div className="max-h-56 overflow-y-auto space-y-1" role="listbox" aria-label="Cases">
            {cases === null ? (
              Array.from({ length: 3 }, (_, index) => <SkeletonLoader key={index} className="h-12 w-full rounded-lg" />)
            ) : matching.length === 0 ? (
              <p className="text-sm text-glass-500 py-3 text-center">
                {cases.length === 0 ? 'No case is open yet.' : 'No case matches that.'}
              </p>
            ) : matching.map((entry) => {
              const picked = value?.mode === 'existing' && value.entry.id === entry.id;
              return (
                <button
                  key={entry.id}
                  type="button"
                  role="option"
                  aria-selected={picked}
                  onClick={() => onChange({ mode: 'existing', entry })}
                  className={cn(
                    'w-full text-left px-3 py-2 rounded-lg border transition-colors',
                    picked ? 'bg-accent-500/15 border-accent-400/40' : 'border-transparent hover:bg-glass-200/[0.06]',
                  )}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-white truncate">{entry.title}</span>
                    {theirs.has(entry.id) && <span className="badge badge-accent shrink-0">this player</span>}
                    {entry.status !== 'open' && <span className="badge badge-neutral shrink-0">{entry.status}</span>}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-glass-500 tabular-nums">
                    <span className="font-mono">{entry.reference}</span>
                    <span aria-hidden>&middot;</span>
                    <span>{formatCount(entry.record_count)} records</span>
                    <span aria-hidden>&middot;</span>
                    <span>{relativeTime(entry.updated_at)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
